import { IconOutlineInfo } from "@/assets/icons";
import { useEnum } from "@/api/services/global/enum";
import { useGetCompanySpecificIndustryList } from "@/api/services/global/master";
import { AutoFilledInput } from "@/features/company/components/AutoFilledInput";
import { FieldGrid, FieldGridItem } from "@/features/company/components/FieldGrid";
import { BILLING_GROUPING_TOOLTIP, ZIP_CODE_PLACEHOLDER } from "@/features/company/constants/sections";
import { TCompanyForm } from "@/features/company/types/company-form";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { FormField } from "@/modules/irohana-components/elements/FormField";
import { Input } from "@/modules/irohana-components/elements/Input";
import { RadioGroupNew } from "@/modules/irohana-components/elements/RadioGroupNew";
import { SelectNew } from "@/modules/irohana-components/elements/SelectNew";
import { Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";

/** 基本情報 section: identity, contact details, the zip-code driven address and the 請求情報 items. */
export const BasicInfoSection: FC = () => {
  const methods = useFormContext<TCompanyForm>();
  const { control } = methods;

  const { data: companyStatusOptions } = useEnum("ECompanyStatus");
  const { data: billingGroupingOptions } = useEnum("EBillingGrouping");
  const { data: travelExpenseBillingOptions } = useEnum("ETravelExpenseBilling");

  // 代表分野 — 特定産業分野（企業）マスタ。非表示のマスタは選択肢に出さず、id は文字列で保持する。
  const { data: companySpecificIndustries } = useGetCompanySpecificIndustryList();
  const companySpecificIndustryOptions = useMemo(
    () => companySpecificIndustries.filter((item) => !item.isHidden).map((item) => ({ value: String(item.id), label: item.companySpecificIndustry })),
    [companySpecificIndustries],
  );

  // 郵便番号 が 999-9999 に一致した時点で 都道府県・市区町村 を自動入力（町名丁目番地号等 は手入力のまま）。
  useLocationInfo<TCompanyForm>({ methods, names: { zipCode: "zipCode", province: "prefecture", municipality: "city" } });

  return (
    <FieldGrid>
      <FieldGridItem span={2}>
        <FormField<TCompanyForm> name="status" control={control} fullWidth>
          <SelectNew fullWidth options={companyStatusOptions} />
        </FormField>
      </FieldGridItem>

      <FormField<TCompanyForm> name="companyName" control={control} fullWidth>
        <Input fullWidth clearable />
      </FormField>
      <FormField<TCompanyForm> name="companyKanaName" control={control} fullWidth>
        <Input fullWidth clearable />
      </FormField>

      <FormField<TCompanyForm> name="contactPerson" control={control} fullWidth>
        <Input fullWidth clearable />
      </FormField>
      <FormField<TCompanyForm> name="companySpecificIndustryId" control={control} fullWidth>
        <SelectNew fullWidth options={companySpecificIndustryOptions} />
      </FormField>

      <FormField<TCompanyForm> name="email" control={control} fullWidth>
        <Input fullWidth clearable transformToHalfWidth />
      </FormField>
      <FormField<TCompanyForm> name="phoneNumber" control={control} fullWidth>
        <Input fullWidth clearable transformToHalfWidth />
      </FormField>

      <FieldGridItem span={2}>
        <FieldGrid columns={3}>
          <FormField<TCompanyForm> name="zipCode" control={control} fullWidth>
            <Input fullWidth clearable transformToHalfWidth placeholder={ZIP_CODE_PLACEHOLDER} />
          </FormField>
          <FormField<TCompanyForm> name="prefecture" control={control} fullWidth>
            <AutoFilledInput />
          </FormField>
          <FormField<TCompanyForm> name="city" control={control} fullWidth>
            <AutoFilledInput />
          </FormField>
        </FieldGrid>
      </FieldGridItem>

      <FieldGridItem span={2}>
        <FormField<TCompanyForm> name="addressDetail" control={control} fullWidth>
          <Input fullWidth clearable />
        </FormField>
      </FieldGridItem>

      <FormField<TCompanyForm> name="billingAddressee" control={control} fullWidth>
        <Input fullWidth clearable />
      </FormField>
      <FormField<TCompanyForm> name="billingLocation" control={control} fullWidth>
        <Input fullWidth clearable />
      </FormField>

      <FormField<TCompanyForm>
        name="billingGrouping"
        control={control}
        fullWidth
        helper={BILLING_GROUPING_TOOLTIP}
        helperContent={
          <Typography variant="cap12" whiteSpace="pre-line">
            {BILLING_GROUPING_TOOLTIP}
          </Typography>
        }
        helperIcon={<IconOutlineInfo />}
      >
        <RadioGroupNew options={billingGroupingOptions} />
      </FormField>
      <FormField<TCompanyForm> name="travelExpenseBilling" control={control} fullWidth>
        <RadioGroupNew options={travelExpenseBillingOptions} />
      </FormField>
    </FieldGrid>
  );
};
